import { chain, keys, sum } from 'lodash';
import { antibioticL2Targets, GetGroup, L2Target, L2TargetOrAssay, L2Targets, nonSixteenSL2Targets } from '../assays';
import { riskClassByOneHealthAssay } from '../assays-temp-96-gene-minor-targets';
import { AssayResult, FullAbundance, NormalisationMode } from '../types';
import { AbundanceOrAssayResult, filterDetected } from '../utils';
import { filterTargetAbundances } from './abundance-stats';

export enum IndexVersion {
  ARI = 'ARI',
  ARGI = 'ARGI',
  RISK = 'RISK',
}

export enum Aggregation {
  GEOMETRIC = 'GEOMETRIC',
  QUADRATIC = 'QUADRATIC',
  ARITHMETIC = 'ARITHMETIC', // non-log space
  ARITHMETIC_MAX = 'ARITHMETIC_MAX',
  KARINA = 'KARINA', // non-log space
  MAX = 'MAX',
  MEDIAN = 'MEDIAN',
}

export enum Scope {
  DETECTED = 'DETECTED',
  ANALYSED = 'ANALYSED',
}

interface RiskScoreNormalizationConstants {
  maxSampleSumEuropeanWWTreated95th: number;
  maxSampleSumGlobalWWRaw95th: number;
}

// 95th percentile approved treated wastewater samples with volume metadata among 126 samples (7/5 asia europe split) as of 1.4.2025
// See karina-scaling.sql and https://3.basecamp.com/5544917/buckets/35562145/card_tables/cards/8499759059
const riskScoreNormalizationConstants: Partial<Record<L2Targets | 'ALL', RiskScoreNormalizationConstants>> = {
  ALL: { maxSampleSumEuropeanWWTreated95th: 3_189_083_611, maxSampleSumGlobalWWRaw95th: 177_001_238_038 },
  AMINOGLYCOSIDE: { maxSampleSumEuropeanWWTreated95th: 2_127_423_989, maxSampleSumGlobalWWRaw95th: 127_022_307_665 },
  BETA_LACTAM: { maxSampleSumEuropeanWWTreated95th: 226_390_410, maxSampleSumGlobalWWRaw95th: 8_068_494_207 },
  MDR: { maxSampleSumEuropeanWWTreated95th: 188_104_835, maxSampleSumGlobalWWRaw95th: 9_333_717_126 },
  MLSB: { maxSampleSumEuropeanWWTreated95th: 213_248_870, maxSampleSumGlobalWWRaw95th: 72_052_154_758 },
  PHENICOL: { maxSampleSumEuropeanWWTreated95th: 31_720_045, maxSampleSumGlobalWWRaw95th: 889_967_357 },
  QUINOLONE: { maxSampleSumEuropeanWWTreated95th: 527_996_941, maxSampleSumGlobalWWRaw95th: 4_727_099_780 },
  SULFONAMIDE: { maxSampleSumEuropeanWWTreated95th: 488_810, maxSampleSumGlobalWWRaw95th: 12_432_482 },
  TETRACYCLINE: { maxSampleSumEuropeanWWTreated95th: 56_793_270, maxSampleSumGlobalWWRaw95th: 9_371_110_743 },
  VANCOMYCIN: { maxSampleSumEuropeanWWTreated95th: 100_762_976, maxSampleSumGlobalWWRaw95th: 757_503_191 },

  // NOTE: the risk score considers two non-antibiotics
  OTHER_MICROBIAL_MARKER: { maxSampleSumEuropeanWWTreated95th: 3_112_970, maxSampleSumGlobalWWRaw95th: 732_985_162 },
  OTHER_RESISTANCE_MARKER: {
    maxSampleSumEuropeanWWTreated95th: 210_138_535,
    maxSampleSumGlobalWWRaw95th: 10_694_002_563,
  },
};

export function getRiskScore(
  abundances: FullAbundance[] | undefined,
  targets: L2TargetOrAssay[] | undefined,
  getGroup: GetGroup,
) {
  const reconstructedAssayResults = abundances?.map(a => ({
    value: a.copiesPerL,
    normalisationMode: NormalisationMode.LITRE_16S,
    assay: a.assay,
  }));
  return calcKarinaRiskScore(reconstructedAssayResults, targets, getGroup);
}

/**
 * Calculate risk score as per https://doi.org/10.1016/j.jhazmat.2021.127621
 */
export function calcKarinaRiskScore(
  assayResults: Array<Pick<AssayResult, 'value' | 'normalisationMode' | 'assay'>> | undefined,
  _targets: L2TargetOrAssay[] | undefined,
  getGroup: GetGroup,
): number | null {
  if (assayResults?.find(a => a.normalisationMode !== NormalisationMode.LITRE_16S)) {
    throw new Error(
      `Unexpected normalisation mode ${assayResults.find(a => a.normalisationMode !== NormalisationMode.LITRE_16S)?.normalisationMode}`,
    );
  }
  if (!assayResults?.length) {
    return null;
  }

  // Default to all genes and keep showing the main risk score when an assay (or other unsupported target combos) is selected
  const targets =
    !_targets || _targets.length !== 1 || _targets.find(t => !nonSixteenSL2Targets.includes(t as L2Target))
      ? nonSixteenSL2Targets
      : _targets;

  const targetResults = filterTargetAbundances(assayResults, targets, getGroup);
  const inScopeResults = targetResults.filter(a => riskClassByOneHealthAssay[a.assay]);
  const inScopeAssays = chain(inScopeResults)
    .map(a => a.assay)
    .uniq()
    .value();
  const inScopeWeights = inScopeAssays.map(a => getRiskWeight(a));
  if (!inScopeResults.length || !inScopeWeights.length) {
    return null;
  }

  // Determine the target key for normalization constants. Default to 'ALL' if multiple targets.
  const normalizationTargetKey: L2Target | 'ALL' = targets.length === 1 ? (targets[0] as L2Target) : 'ALL';

  // Retrieve normalization constants based on the determined key
  const normalizationConstants = riskScoreNormalizationConstants[normalizationTargetKey];
  if (!normalizationConstants) {
    throw new Error(`Unexpectedly missing normalization constants for target: ${normalizationTargetKey}`);
  }
  const { maxSampleSumEuropeanWWTreated95th, maxSampleSumGlobalWWRaw95th } = normalizationConstants;

  // Calculate the number of genes relevant to the normalization target in the One Health panel
  const allRiskAssays = keys(riskClassByOneHealthAssay);
  const maxNumAssaysForTargets =
    normalizationTargetKey === 'ALL'
      ? allRiskAssays.length
      : allRiskAssays.filter(assay => getGroup(assay) === normalizationTargetKey).length;

  // NOTE: Use a constant weightNormaliser instead of calculating it based on the in scope weights.
  // This ensures that antibiotic specific risk scores consider the risks classes of the subset of the genes for the antibiotic.
  // Previously, we normalised based on in scope assays, and it resulted in both antibiotic A (with two class I genes) and antibiotic B (with two class IV genes) getting the same score (as if both had class I genes)
  const weightNormaliser = 1.6047904192; // 67 / 41.75 = 1.6047904192 for 96 gene panel (67 args, sum weights 41.75)
  const weights = inScopeResults.map(a => getRiskWeight(a.assay));

  // Normalise for the number of analysed genes (and values) for target(s), in case
  // - all genes weren't analysed
  // - samples were analysed multiple times
  const numAnalysedValues = inScopeResults.length;
  const numResultsNormaliser = numAnalysedValues ? maxNumAssaysForTargets / numAnalysedValues : 1;

  // Assume 0 instead of LOQ for undetected genes, so that we can show a zero risk score (by definition) for samples with no detected genes.
  const inScopeValues = inScopeResults.map(r => r.value || 0);

  // Karina's Step 2 - Scaling
  // - minArgCopyNumber = y_min = sumMin = 0
  // - Approximate global 'y_max' copy numbers in 67 One Health package ARGs across approved european sw samples as of 2025-04
  //   - maxArgCopyNumber: Top 95th percentile signle ARG copy number from db (normalised as if all samples had 67 ARGs)
  //   - maxSum: Top 95th percentile sample sum of all ARG copy numbers from db
  //     (ballpark max for the sum of in scope ARGs in the worst sample globally, best effort normalised as if all samples had 67 ARGs)

  // NOTE: multiplying this with gene count has to reach maxSampleSumEuropeanWWTreated95th across all antibiotics globally and for each antibiotic.
  // We use top 98th percentile to allow very high abundance genes to contribute up to 2.5x their share (the amount what would be required from each gene to reach 100 overall),
  // and thus compensate for some less abundant genes
  const maxArgCopyNumber = 7_377_126_682;
  const globalSumNormaliser = maxArgCopyNumber / maxSampleSumEuropeanWWTreated95th;
  const scaledValues = inScopeValues.map(v => Math.min(1, Math.max(0, v / maxArgCopyNumber)));
  //                                                   ^------------------^
  // - maxArgCopyNumber caps the individual ARG copy number to the top 5% highest in the database globally
  // - As long as the normalised gene copy number does not get capped to 1 its effect will be reversed by multiplying with maxArgCopyNumber (globalSumNormaliser's nominator)
  // - However, this ends up having a significant effect because the scale of different genes vary widely, some are very high while many are at zero.

  const weightedValues = scaledValues.map((v, i) => weights[i] * v);
  const avgScaledValues = sum(weightedValues) * numResultsNormaliser * weightNormaliser;
  const normalisedSum = avgScaledValues * globalSumNormaliser;

  // Calculate piecewise score
  const score = calculatePiecewiseScore(normalisedSum, maxSampleSumEuropeanWWTreated95th, maxSampleSumGlobalWWRaw95th);

  // if (targets.length === 1 && targets[0] === 'TETRACYCLINE') {
  //   console.log('TETRACYCLINE');
  //   console.log('maxSampleSumGlobalWWRaw95th', maxSampleSumGlobalWWRaw95th);
  //   console.log('maxSampleSumEuropeanWWTreated95th', maxSampleSumEuropeanWWTreated95th);
  //   console.log('maxArgCopyNumber', maxArgCopyNumber);
  //   console.log('globalSumNormaliser', globalSumNormaliser);
  //   console.log('--------------------------------');
  //   console.log('maxNumAssaysForTargets', maxNumAssaysForTargets);
  //   console.log('numAnalysedValues', numAnalysedValues);
  //   console.log('numResultsNormaliser', numResultsNormaliser);
  //   console.log('--------------------------------');
  //   console.log('weightNormaliser', weightNormaliser);
  //   console.log('weights', weights);
  //   console.log('inScopeResults', inScopeResults);
  //   console.log('inScopeValues', inScopeValues);
  //   console.log('scaledValues', scaledValues);
  //   console.log('weightedValues', weightedValues);
  //   console.log('avgScaledValues', avgScaledValues);
  //   console.log('normalisedSum', normalisedSum);
  //   console.log('score', score);
  // }

  // Ensure the score is within the valid range [0, 100]
  return Math.min(100, Math.max(0, score));
}

function calculatePiecewiseScore(
  normalisedSum: number,
  maxSampleSumEuropeanWWTreated95th: number,
  maxSampleSumGlobalWWRaw95th: number,
): number {
  if (normalisedSum <= 0) {
    return 0;
  }

  if (normalisedSum <= 1) {
    // Scale 0 to 1 maps linearly to score 0 to 10
    return 10 * normalisedSum;
  }

  // Scale 1 to ratioGlobalEu maps linearly to score 10 to 100
  // ratioGlobalEu defines the upper bound of the input range for the second linear segment
  const ratioGlobalEu = maxSampleSumGlobalWWRaw95th / maxSampleSumEuropeanWWTreated95th;

  // Calculate the proportion along the second segment's input range [1, ratioGlobalEu]
  const positionInSecondSegment = (normalisedSum - 1) / (ratioGlobalEu - 1);
  // Map this proportion to the second segment's output score range [10, 100]
  return 10 + 90 * positionInSecondSegment;
}

export function getAbundancesInScope<A extends AbundanceOrAssayResult>(
  abundances: A[],
  key: 'relative' | 'absolute' | 'copiesPerL',
  scope: Scope,
  allowAnyGenes: boolean,
  getGroup: GetGroup,
  targets?: L2TargetOrAssay[],
) {
  const correctTargets = targets ? targets : antibioticL2Targets;
  const filteredAbundances = targets ? filterTargetAbundances<A>(abundances, correctTargets, getGroup) : abundances;
  const filteredRiskClass = filteredAbundances.filter(a => allowAnyGenes || riskClassByOneHealthAssay[a.assay]);
  return scope === Scope.DETECTED ? filterDetected(filteredRiskClass, key) : filteredRiskClass;
}

export function getValues<A extends AbundanceOrAssayResult>(
  abundances: A[],
  key: 'relative' | 'absolute' | 'copiesPerL',
  scope: Scope,
) {
  return scope === Scope.DETECTED
    ? abundances.map(a => ('value' in a ? (a.value as number) : (a[key] as number)))
    : abundances.map(a => ('value' in a ? a.value || 0 : a[key] || 0));
}

export function geometricMean(values: number[]): number {
  // Use log space to avoid overflow when multiplying large numbers
  const positiveValues = values.filter(v => v > 0);
  if (positiveValues.length === 0) {
    return 0;
  }

  const logSum = positiveValues.reduce((acc, v) => acc + Math.log(v), 0);
  return Math.exp(logSum / positiveValues.length);
}

export function getRiskWeight(assay: string) {
  const riskClass = riskClassByOneHealthAssay[assay];
  switch (riskClass) {
    case 'I':
      return 1;
    case 'II':
      return 0.75;
    case 'III':
      return 0.5;
    case 'IV':
      return 0.25;
    case undefined:
      throw Error(`Unexpected assay in risk weighting: ${assay}`);
  }
}
